import type { SensoryEvent, SensoryEventType } from './SensoryEventBus'

type AudioPreferences = {
  enabled: boolean
  reducedMotion: boolean
  volume: number
}

type CueConfig = {
  url: string
  cooldownMs: number
}

const DEFAULT_PREFERENCES: AudioPreferences = {
  enabled: true,
  reducedMotion: false,
  volume: 0.42,
}

const CUES: Partial<Record<SensoryEventType, CueConfig>> = {
  SCAN_SUCCESS: { url: new URL('../../assets/notification-tones/soft-ping.wav', import.meta.url).href, cooldownMs: 180 },
  SCAN_ERROR: { url: new URL('../../assets/notification-tones/low-pulse.wav', import.meta.url).href, cooldownMs: 260 },
  RAID_COMPLETE: { url: new URL('../../assets/notification-tones/clear-chime.wav', import.meta.url).href, cooldownMs: 900 },
  MUTATION_SUCCESS: { url: new URL('../../assets/notification-tones/soft-ping.wav', import.meta.url).href, cooldownMs: 220 },
  MUTATION_ERROR: { url: new URL('../../assets/notification-tones/low-pulse.wav', import.meta.url).href, cooldownMs: 300 },
}

class AudioCueServiceController {
  private preferences = DEFAULT_PREFERENCES
  private pool = new Map<string, HTMLAudioElement[]>()
  private lastByType = new Map<SensoryEventType, number>()
  private unlocked = false

  configure(preferences: Partial<AudioPreferences>): void {
    this.preferences = { ...this.preferences, ...preferences }
  }

  preload(types: SensoryEventType[] = ['SCAN_SUCCESS', 'SCAN_ERROR', 'RAID_COMPLETE']): void {
    if (typeof Audio === 'undefined') return
    for (const type of types) {
      const cue = CUES[type]
      if (!cue || this.pool.has(cue.url)) continue
      this.pool.set(cue.url, Array.from({ length: 2 }, () => {
        const audio = new Audio(cue.url)
        audio.preload = 'auto'
        audio.volume = this.preferences.volume
        return audio
      }))
    }
  }

  unlock(): void {
    if (this.unlocked || typeof document === 'undefined') return
    this.unlocked = true
    this.preload()
  }

  async handle(event: SensoryEvent): Promise<void> {
    if (event.signal?.aborted) return
    const cue = CUES[event.type]
    if (!cue || !this.canRun(event.type, cue)) return
    this.preload([event.type])
    const audio = this.nextAudio(cue.url)
    if (!audio) return
    audio.volume = this.preferences.volume
    audio.currentTime = 0
    await audio.play().catch(() => undefined)
  }

  private canRun(type: SensoryEventType, cue: CueConfig): boolean {
    if (!this.preferences.enabled || this.preferences.reducedMotion) return false
    const now = Date.now()
    const last = this.lastByType.get(type) ?? 0
    if (now - last < cue.cooldownMs) return false
    this.lastByType.set(type, now)
    return true
  }

  private nextAudio(url: string): HTMLAudioElement | null {
    const entries = this.pool.get(url)
    if (!entries?.length) return null
    const idle = entries.find((entry) => entry.paused || entry.ended) ?? entries[0]
    return idle ?? null
  }
}

export const AudioCueService = new AudioCueServiceController()
